import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ScrollText, Save, ExternalLink, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { slug: 'cgv', label: 'CGV', path: '/cgv' },
  { slug: 'mentions-legales', label: 'Mentions legales', path: '/mentions-legales' },
  { slug: 'confidentialite', label: 'Confidentialite', path: '/confidentialite' },
];

export default function AdminLegal() {
  const navigate = useNavigate();
  const [active, setActive] = useState('cgv');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('admin_token');
  const api = useCallback((method, url, data) =>
    axios({ method, url: `${API}${url}`, data, headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadPage = useCallback(async (slug) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/legal/${slug}`);
      setTitle(r.data.title || '');
      setContent(r.data.content || '');
      setUpdatedAt(r.data.updated_at);
    } catch { toast.error('Erreur de chargement de la page'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadPage(active);
  }, [token, navigate, active, loadPage]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api('put', `/api/admin/legal/${active}`, { title, content });
      setUpdatedAt(r.data.updated_at);
      toast.success('Page enregistree et publiee');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const activePage = PAGES.find(p => p.slug === active);

  return (
    <div className="min-h-screen bg-[#F2F0E9]">
      <header className="bg-[#2C3E30] text-[#F2F0E9] sticky top-0 z-40">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button data-testid="admin-legal-back" variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9] hover:bg-white/10 p-2 h-auto">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <ScrollText className="w-5 h-5 text-[#C4A962]" strokeWidth={1.5} />
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl">Pages legales</h1>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-8 space-y-6">
        <div className="flex flex-wrap gap-2" data-testid="admin-legal-tabs">
          {PAGES.map(p => (
            <Button
              key={p.slug}
              data-testid={`admin-legal-tab-${p.slug}`}
              onClick={() => setActive(p.slug)}
              variant={active === p.slug ? 'default' : 'outline'}
              className={`rounded-full text-sm ${active === p.slug ? 'bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]' : 'text-[#2C3E30]'}`}
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs text-[#595959] block mb-1.5">Titre de la page</label>
              <Input data-testid="admin-legal-title-input" value={title} onChange={e => setTitle(e.target.value)} className="rounded-lg border-[#E5E5E0]" disabled={loading} />
            </div>
            <div className="flex items-end gap-2 pt-5">
              <a href={activePage.path} target="_blank" rel="noreferrer">
                <Button variant="outline" data-testid="admin-legal-preview-btn" className="rounded-full text-sm">
                  <ExternalLink className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Voir la page
                </Button>
              </a>
              <Button data-testid="admin-legal-save-btn" onClick={save} disabled={saving || loading} className="rounded-full bg-[#8A9A5B] text-white hover:bg-[#7a8a4d]">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" strokeWidth={1.5} />}
                Enregistrer et publier
              </Button>
            </div>
          </div>

          {updatedAt && (
            <p className="text-xs text-[#8A8A85]" data-testid="admin-legal-updated-at">
              Derniere publication : {new Date(updatedAt).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
            </p>
          )}

          <div className="p-3 rounded-lg bg-[#F5F5F0] text-xs text-[#595959] flex gap-2" data-testid="admin-legal-help">
            <Info className="w-4 h-4 shrink-0 text-[#8A9A5B]" strokeWidth={1.5} />
            <div className="space-y-1">
              <p>Mise en forme <strong>Markdown</strong> : <code>## Titre</code>, <code>### Sous-titre</code>, <code>**gras**</code>, <code>*italique*</code>, <code>- liste</code>, tableaux <code>| col | col |</code>.</p>
              <p>Placeholders dynamiques (CGV) remplaces automatiquement par vos saisons configurees : <code>{'{{SAISON_HAUTE}}'}</code>, <code>{'{{SAISON_INTERMEDIAIRE}}'}</code>, <code>{'{{SAISON_BASSE}}'}</code>.</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#8A9A5B]" /></div>
          ) : (
            <textarea
              data-testid="admin-legal-content-textarea"
              value={content}
              onChange={e => setContent(e.target.value)}
              spellCheck={false}
              className="w-full min-h-[560px] p-4 rounded-lg border border-[#E5E5E0] bg-[#FAFAF8] font-mono text-xs leading-relaxed text-[#2C3E30] focus:outline-none focus:ring-2 focus:ring-[#8A9A5B]/40 resize-y"
            />
          )}
        </div>
      </main>
    </div>
  );
}
